import { useEffect, useState, useCallback } from 'react';
import { get } from '../../lib/api-client';

interface TeamEntry {
  id: string;
  name: string;
  ownerId: string;
  ownerEmail?: string;
  memberCount: number;
  maxMembers: number;
  createdAt: string;
}

interface TeamsResponse {
  data: TeamEntry[];
  pagination: { page: number; pageSize: number; total: number };
}

export function Teams() {
  const [teams, setTeams] = useState<TeamEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      const data = await get<TeamsResponse>(`/v1/admin/teams?${params.toString()}`);
      setTeams(data.data);
      setTotal(data.pagination.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void fetchTeams();
  }, [fetchTeams]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-ll-text">Teams</h2>

      {error && (
        <div className="bg-ll-error/10 border border-ll-error/30 rounded-lg p-4 text-sm text-ll-error">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ll-muted">Loading...</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ll-border text-left">
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Name</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Owner</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Members</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Created</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team) => (
                  <tr key={team.id} className="border-b border-ll-border/50 hover:bg-white/[0.02]">
                    <td className="py-2.5 px-3 text-ll-text font-medium">{team.name}</td>
                    <td className="py-2.5 px-3 text-ll-muted text-xs font-mono">
                      {team.ownerEmail ?? team.ownerId}
                    </td>
                    <td className="py-2.5 px-3 text-ll-text">
                      {team.memberCount} / {team.maxMembers}
                    </td>
                    <td className="py-2.5 px-3 text-ll-muted">
                      {new Date(team.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {teams.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-ll-muted">
                      No teams found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-ll-muted">{total} teams total</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 rounded border border-ll-border text-xs text-ll-muted hover:text-ll-text disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-ll-muted py-1.5">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 rounded border border-ll-border text-xs text-ll-muted hover:text-ll-text disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
